import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useInspectionService } from "../../service/vehicle/inspectionService";
import { useSnackbar } from "notistack";

export const useInspectionOperations = () => {
  const inspectionService = useInspectionService();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  /**
   * Query para obtener entidades con filtros
   */
    const useEntities = (filters = {}, type = 'vehicles') => {
    return useQuery({
      queryKey: [type, filters],
      queryFn: () => inspectionService.getEntities(filters, type),
      keepPreviousData: true,
    });
  };

  /**
   * Mutación para procesar archivos Excel
   */
  const useProcessExcelFiles = () => {
    return useMutation({
      mutationFn: (files) => {
        const formData = new FormData();
        files.forEach(file => formData.append('files', file));
        return inspectionService.uploadAndProcessExcel(formData);
      },
      onSuccess: () => {
        enqueueSnackbar('Archivos procesados correctamente', { variant: 'success' });
      },
      onError: (error) => {
        const errorMsg = error.message || 'Error al procesar los archivos Excel';
        enqueueSnackbar(errorMsg, { variant: 'error' });
      }
    });
  };

  /**
   * Mutación para guardar entidades
   */
  const useSaveEntities = () => {
    return useMutation({
      mutationFn: (payload) => inspectionService.saveInspections(payload),
      onSuccess: (data) => {
        const { saved, errors, duplicates } = data.results;
        
        if (saved.length > 0) {
          enqueueSnackbar(`${saved.length} registros guardados exitosamente`, { variant: 'success' });
        }
        
        if (errors.length > 0) {
          enqueueSnackbar(`${errors.length} registros con errores`, { variant: 'warning' });
        }
        
        if (duplicates.length > 0) {
          enqueueSnackbar(`${duplicates.length} registros duplicados encontrados`, { variant: 'info' });
        }
        
        queryClient.invalidateQueries(['vehicles']);
        queryClient.invalidateQueries(['operators']);
      },
      onError: (error) => {
        const errorMsg = error.message || 'Error al guardar registros';
        enqueueSnackbar(errorMsg, { variant: 'error' });
      }
    });
  };

  /**
   * Mutación para verificar entidades existentes
   */
  const useCheckExistingEntities = () => {
    return useMutation({
      mutationFn: (data) => inspectionService.checkExistingEntities(data),
    });
  };

  /**
   * Mutación para actualizar una entidad
   */
  const useUpdateEntity = () => {
    return useMutation({
      mutationFn: ({ id, data, type = 'vehicle' }) => inspectionService.updateEntity(id, data, type),
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries([variables.type === 'operator' ? 'operators' : 'vehicles']);
        enqueueSnackbar(`${variables.type === 'operator' ? 'Operador' : 'Vehículo'} actualizado correctamente`, { variant: 'success' });
      },
      onError: (error) => {
        const errorMsg = error.message || 'Error al actualizar registro';
        enqueueSnackbar(errorMsg, { variant: 'error' });
      }
    });
  };

  /**
   * Mutación para eliminar una entidad
   */
  const useDeleteEntity = () => {
    return useMutation({
      mutationFn: ({ id, type = 'vehicle' }) => inspectionService.deleteEntity(id, type),
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries([variables.type === 'operator' ? 'operators' : 'vehicles']);
        enqueueSnackbar(`${variables.type === 'operator' ? 'Operador' : 'Vehículo'} eliminado correctamente`, { variant: 'success' });
      },
      onError: (error) => {
        const errorMsg = error.message || 'Error al eliminar registro';
        enqueueSnackbar(errorMsg, { variant: 'error' });
      }
    });
  };

  /**
   * Mutación para exportar a Excel
   */
    const useExportToExcel = () => {
    return useMutation({
      mutationFn: ({ type = 'vehicles', exportType, ...params }) => 
        inspectionService.exportToExcel(params, type, exportType),
      onSuccess: (data) => {
        // Crear y descargar archivo
        const blob = new Blob([data], { 
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
        });
        
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `inspecciones_${new Date().toISOString().split('T')[0]}.xlsx`;
        document.body.appendChild(a);
        a.click();
        
        // Limpiar 
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
        enqueueSnackbar('Excel generado correctamente', { variant: 'success' });
      },
      onError: (error) => {
        const errorMsg = error.message || 'Error al exportar a Excel';
        enqueueSnackbar(errorMsg, { variant: 'error' });
      }
    });
  };

  return {
    useEntities,
    useProcessExcelFiles,
    useSaveEntities,
    useCheckExistingEntities,
    useUpdateEntity,
    useDeleteEntity,
    useExportToExcel
  };
};